import clsx from "clsx";
import i18next from "i18next";
import React, { useEffect, useState } from "react";
import { Flex } from "antd";
import { useQueryClient } from "react-query";

import RedesignButton from "~/shared/ui/Fields/RedesignButton";
import { useAppeals, useSetAppeal } from "~/entity/Appeals";
import {
  APPEAL_STATUS,
  IAppealSchema,
  RESOLUTION_STATUS,
  TAppealFilter,
} from "~/entity/Appeals/model/schema";
import { useURLParamsGetter } from "~/hooks/useURLParamsSetter";
import { usePermissions } from "~/shared/context/PermissionContext";
import { Select } from "~/shared/ui/Select";

import s from "./ResolutionStatusFrontendController.less";

interface IResolutionStatusFrontendControllerProps {
  status: IAppealSchema["resolutionStatusFrontend"];
  appealStatus: IAppealSchema["appealStatusFrontend"];
  onOpenResolveAppealModal: () => void;
  onOpenAppealCommentModal: () => void;
  onOpenEditorAppealModal: () => void;
  appealId: number;
}

export const ResolutionStatusFrontendController = (
  props: IResolutionStatusFrontendControllerProps,
) => {
  const {
    status,
    appealStatus,
    onOpenResolveAppealModal,
    onOpenAppealCommentModal,
    onOpenEditorAppealModal,
    appealId,
  } = props;

  const { hasPermission } = usePermissions();

  const queryClient = useQueryClient();
  const { setAppealReview } = useSetAppeal();
  const { queryKeys } = useAppeals();
  const { currentId, ...settedFilter } = useURLParamsGetter<
    TAppealFilter & { currentId: string }
  >();

  const adminResolveStatusOptionsMap = {
    [RESOLUTION_STATUS.sent]: {
      label: "Подана",
      value: RESOLUTION_STATUS.sent,
    },
    [RESOLUTION_STATUS.review]: {
      label: "На рассмотрении",
      value: RESOLUTION_STATUS.review,
    },
    [RESOLUTION_STATUS.needInfo]: {
      label: "Запрос доп. информации",
      value: RESOLUTION_STATUS.needInfo,
    },
  };
  const adminResolveStatusOptions = Object.values(adminResolveStatusOptionsMap);

  const [adminStatus, setAdminStatus] = useState(
    adminResolveStatusOptionsMap[status],
  );

  useEffect(() => {
    if (adminResolveStatusOptionsMap[status]) {
      setAdminStatus(adminResolveStatusOptionsMap[status]);
    }
  }, [status]);

  let statusText = i18next.t(`appeal.resolutionStatus.${status}`, {
    defaultValue: "-",
  });
  const isRejected = status === RESOLUTION_STATUS.rejected;
  const isAccepted = status === RESOLUTION_STATUS.accepted;
  const isSent = status === RESOLUTION_STATUS.sent;
  const isNone = status === RESOLUTION_STATUS.none;
  const isOnReview = status === RESOLUTION_STATUS.review;

  switch (status) {
    case RESOLUTION_STATUS.none:
      if (appealStatus === APPEAL_STATUS.needInfo) {
        statusText = "Запрошена доп. информация";
      } else {
        return <span>-</span>;
      }
  }

  const onStatusChangeOfReview = () => {
    setAppealReview.mutate(appealId, {
      onSuccess: () => {
        setAdminStatus(adminResolveStatusOptionsMap[RESOLUTION_STATUS.review]);
        queryClient.invalidateQueries(queryKeys.allWOFilter);
      },
    });
  };

  const onStatusChange = (value) => {
    switch (value) {
      case RESOLUTION_STATUS.needInfo: {
        onOpenAppealCommentModal();
        break;
      }
      case RESOLUTION_STATUS.review: {
        onStatusChangeOfReview();
        break;
      }
    }
  };

  const hasAppealClosePermission = hasPermission(
    "employeeAccounting.appeal.close",
  );

  return (
    <Flex
      className={clsx(s.resolutionContainer, s.resolutionContainerTooltip)}
      gap={4}
      vertical
    >
      {hasAppealClosePermission && (isSent || isOnReview) ? (
        <Select
          className={s.resolutionSelect}
          variant="borderless"
          style={{ width: 130 }}
          options={adminResolveStatusOptions}
          value={adminStatus}
          onChange={onStatusChange}
          optionRender={(option) => (
            <span className={s.resolutionSelectItem}>{option.label}</span>
          )}
        />
      ) : (
        <>
          <span>{statusText}</span>
          {(isAccepted || isRejected) && hasAppealClosePermission && (
            <RedesignButton
              theme="main"
              txtBtn={"изменить решение"}
              onClick={onOpenEditorAppealModal}
              customClassName={s.submitButton}
            />
          )}
        </>
      )}
      {hasAppealClosePermission && isOnReview && (
        <RedesignButton
          theme="main"
          txtBtn={i18next.t("appeal.resolveAppeal", {
            defaultValue: "Принять решение",
          })}
          onClick={onOpenResolveAppealModal}
          customClassName={s.submitButton}
        />
      )}
    </Flex>
  );
};
